// tslint:disable: no-unused-expression
import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';

import { CallApiMethodsService } from 'src/app/services/AsynchronousRequestServices/call-api-methods.service';
import { GetCurrentThemeService } from 'src/app/services/TemplateServices/get-current-theme.service';
import { PrettyTooltipService } from 'src/app/services/TemplateServices/pretty-tooltip.service';

import { NormalizeTextPipe } from 'src/app/pipes/normalize-text.pipe';
import { UncommentHtmlPipe } from 'src/app/pipes/uncomment-html.pipe';

import { REGEXP } from 'src/app/constants/reg-exp';

import { TableElements, TableTooltipedField, TableFile, HSColumn } from 'src/app/interfaces/datatable';
import { StringValues, BooleanValues, AnyValues } from 'src/app/interfaces/undetermined';

@Component({
    templateUrl: './table.component.html',
    styleUrls: ["./table.component.scss"],
    providers: [UncommentHtmlPipe],
    selector: 'app-table',
})
export class TableComponent implements OnChanges, OnDestroy {
    // classes: Clases que se agregarán a la tabla
    @Input() classes = 'highlight z-depth-1';
    // headers: Matriz con las cabeceras de la tabla, cada array corresponde a una fila
    @Input() headers: string[][] = [[]];
    // body: Matriz con el cuerpo de la tabla, cada array/objeto corresponde a una fila
    @Input() body: ({ [undetermindeIndex: string]: any } | string[])[] = [];
    // footers:Matriz con el pié de página de la tabla, cada array corresponde a una fila
    @Input() footers: string[][] = [[]];
    /* colspan: Espacio que ocuparán las columnas de la cabecera o cuerpo o pie de página
        Para la cabecera será una matriz donde se establece por cada fila el espacio que ocupará cada columna
        Para el cuerpo será un array donde se establece para todas las filas el espacio que ocupará cada columna
        Para el pié de página será una matriz donde se establece por cada fila el espacio que ocupará cada columna
        Ejemplo → headers [["NombreTabla"],["IDS","VALOR","TOTAL"]], body [[1,2],[2,3]], footers [["TOTAL", 5]]
        └                [[[      3      ],[  1,     1,      1   ]],      [ 1,1       ],         [[   2     1]]]
        └ Resultado ¬
        Header  →   |     NombreTabla     |
        Header  →   | IDS | VALOR | TOTAL |
        Body    →   |  1  |       2       |
        Body    →   |  2  |       3       |
        Footer  →   |    TOTAL    |   5   |
    */
    @Input() colspan: ([number[], number, number[]] | null[][])[] = [[[]], [], [[]]];
    /* hClasses: Clases para agregar a cada columna de las filas de la cabecera
        Estructura:
            th: Clases que se aplicarán a todas las columnas la primera fila
            td: Clases que se aplicarán a todas las columnas las siguientes filas
    */
    @Input() hClasses: TableElements = { th: "", td: "" };
    /* bClasses: Clases para agregar a cada columna del cuerpo
        Estructura:
            td: Clases que se aplicarán a todas las columnas de la fila
    */
    @Input() bClasses: TableElements = { td: "" };
    /* fClasses: Clases para agregar a cada columna de las filas del pie de página
        Estructura:
            th: Clases que se aplicarán a todas las columnas la primera fila
            td: Clases que se aplicarán a todas las columnas las siguientes filas
    */
    @Input() fClasses: TableElements = { th: "", td: "" };
    // tLength: Cantidad de columnas que tendrá la tabla, para acomodar texto "Sin Información"
    @Input() tLength = 0;
    /* config: Opciones que se activarán para la tabla
        0. Activar/Desactivar Todas las opciones
        1. Seleccionar cantidad Filas a Mostrar (El 3 debe estar activado)
        2. Buscador
        3. Paginador
    */
    @Input() config: boolean[] = [true, false, false, false];
    // tooltip: Activar el componente de los tooltips
    @Input() tooltip = false;
    /* tooltips: columnas de la cabecera o pie de página en donde se activará botón de más información
        Para la cabecera será una matriz donde se establece por cada fila si la columna mostrará información
        Para el pié de página será una matriz donde se establece por cada fila si la columna mostrará información
        Estructura:
            text: Texto a mostrar en el cuerpo tooltip
            direction: Posicionamiento vertical u horizontal del tooltip
            icon: nombre del icono en https://material.io/icons
        Ejemplo → headers [["NombreTabla"],["IDS","VALOR","TOTAL"]], footers [["TOTAL",  5  ]]
        └                [[[    null     ],[tool1,  null , tool2 ]],         [[ tool3 , null]]]
        └ tool1, tool2, tool3 → Se refiere a la estructura
        └ Resultado ¬
        Header  →   |       NombreTabla       |
        Header  →   | IDS ® | VALOR | TOTAL ® |
        Footer  →   |     TOTAL   ® |    5    |
        └ ® → Símbolifica el tooltip
    */
    @Input() tooltips: TableTooltipedField[][][] = [[[]], [[]]];
    /* highlight: Para el cuerpo será un objeto cuya llave será el índice de una columna y el valor será la estructura correspondiente para resaltar un campo
        [índice]: Índice de Columna
        [valor]: Objeto
            format: Formateador a utilizar en el valor de la columna
            conditions: Lista de Objetos (para formulas diferentes)
                type: Opciones
                    'reduce': Se agregará la clase de acuerdo a la evaluación de resultados de todas las opciones
                    'index': Se agregará la clase de acuerdo al primer resultado verdadero
                options: Objeto
                    [índice]: Opciones
                        ('true', 'false'): índices válidos cuando type es 'reduce'
                        '-1': Cuando type es 'index', llave para las clases por defecto
                        [número]: Cuando type es 'index', por cada item de cases la clase a agregar
                    [valor]: Clase a agregar de acuerdo al resultado
                spreadsheetOptions: Igual que options, en vez de clases especificar el color (hexadecimal) y relleno (color, background), para cuando se vaya a exportar
                cases: Lista de Objetos (para fomulas del mismo tipo)
                    value: Valor a comparar;
                    operator: Operador de comparación;
                    index: Índice de la columna para obtener valor a comparar;
                    format: Formateador a utilizar en el índice de la columna;
                    next: Expresión a utilizar cuando type es 'reduce' y hay más de un cases;
                    cast: Obligar valores a un mismo tipo;
    */
    @Input() highlight: HSColumn;
    /* replaceHighlight: Para el cuerpo será un array que por cada columna especificará un valor numérico para resaltar por color y además reemplazarlo por un símbolo
        └ El resultado dependrá del pipe "getAbsolutePercentage" (si es menor devolverá (↓, rojo), si es mayor (↑, verde))
        Ejemplo → body [[  1  , 2, -15],[3,-8,5]]
        └               [null , 0,  0 ] → Se replicará por cada fila
        └ Resultado ¬
        Body    →   | 1 | ↑ | ↓ |
        Body    →   | 3 | ↓ | ↑ |
    */
    @Input() replaceHighlight: number[];
    // highlightColors: Opciones para devolver según el resultado de "getAbsolutePercentage", cuando highlight se establece
    @Input() highlightColors: string[] = ["danger", "warning", "success"];
    // getValue: Si está definido permite obtener información de la selección de una fila del cuerpo
    @Input() getValue: boolean;
    // sendValue: enviará un array con los valores de la fila seleccionada cuando getValue se establece
    @Output() sendValue: EventEmitter<({ [undetermindeIndex: string]: any } | string[])> = new EventEmitter();
    // autocheck: si se establece, la primera fila de la tabla se seleccionará cuando getValue se establece
    @Input() autocheck: boolean;
    // responsive: si se establece, activará el evento mousewheel en desplazamiento horizontal
    @Input() responsive: boolean;
    // trBoldStyle: Para el cuerpo, será un listado de índices de filas que aplicará negrilla
    @Input() trBoldStyle: boolean[];
    // trBorderStyle: Para el cuerpo, será un listado de índices de filas que aplicará borde inferior
    @Input() trBorderStyle: boolean[];
    // headerFixed: si se establece, la cabecera de la tabla se mantendrá fija en desplazamiento vertical
    @Input() headerFixed: boolean;
    // hideElements: Para el cuerpo será un listado de índices de columnas que se ocultarán
    @Input() hideElements: number[];
    // hideTitles: Para las cabeceras será un listado de índices de columnas que se ocultarán
    @Input() hideTitles: number[];
    /* files: Opciones de descarga
        Estructura:
            type: Hoja de Cálculo
            name: Nombre del archivo
            extension: Extensión del archivo
            sheets: Cuando type es Hoja de Cálculo, nombre de cada libro en la hoja
    */
    @Input() files: TableFile[];
    // download: Activa la descarga de la tabla cuando suva lor cambia y files esta definido
    @Input() download: number;
    /* tdClasses: Para el cuerpo, añade clases a una columna especificada en todas las filas
        Estructura: Objeto
            [propiedad] -> índice de la columna
            [valor] -> clases que se agregarán
    */
    @Input() tdClasses: StringValues;
    /* thClasses: Para la cabcecera, añade clases a una columna especificada por cada fila
        Estructura: Objeto
            [propiedad] -> índice de la columna
            [valor] -> clases que se agregarán
    */
    @Input() thClasses: StringValues[];

    // hiddenGroups: Activa la opción de mostrar/ocultar columas
    @Input() hiddenGroups: number[][];

    // fileAction: Comparte información de la descarga, si está definido la descarga se completo, de lo contrario se canceló
    @Output() fileAction: EventEmitter<boolean> = new EventEmitter();
    // bodyCallbacks: Listado de formateador personalizado para las columnas del cuerpo
    @Input() bodyCallbacks: { [undetermindeIndex: string]: (((row: (AnyValues | string[]), col: string, rowIndex: number, colIndex: number) => string) | null) };

    constructor(
        private callApiMethods: CallApiMethodsService,
        public currentTheme: GetCurrentThemeService,
        public prettyTooltip: PrettyTooltipService,
        private uncommentHtml: UncommentHtmlPipe,
        private builder: FormBuilder,
    ) { this.keyTable = this.generateHexString(8); }
    // ↑ Genera el id único del componente

    // list: Opciones de filas a mostrar
    @Input() list: number[] = [10, 25, 50];
    // table: Guarda el cuerpo de la tabla original
    table: ({ [undetermindeIndex: string]: any } | string[])[] = [];
    // tableBody: Guarda la información el cuerpo de la tabla con filtros aplicados
    tableBody: ({ [undetermindeIndex: string]: any } | string[])[] = [];
    // pags: Opciones del paginador
    pags: number[] = [];
    // slice: Cantidad de registros por página
    slice: number | string;
    // currentPage: Página actual de seleccionada
    currentPage = 1;
    // keyTable: Id único del componente
    keyTable: string;
    // variableConfig: Configuración inicial de config
    variableConfig: boolean[];
    // variableSlice: Configuración inicial de slice
    variableSlice: number;
    // Opciones del formulario de descarga de tabla
    filterOptions: FormGroup;

    // variableHideElements: Configuración inicial de hideElements
    variableHideElements: number[];
    // variableHideTitles: Configuración inicial de hideTitles
    variableHideTitles: number[];

    // ngOnChanges(): Método que está pendiente de modificaciones externas para aplicar cambios a la tabla
    async ngOnChanges(event: SimpleChanges) {
        // ↓ verifica si el cuerpo de la tabla está definido
        if (event.body) {
            // ↓ verifica que no sea el primer cambio recibido del cuerpo de la tabla ya que en peticiones asincronas siempre llega vacío
            if (!event.body.firstChange) {
                // ↓ verifica si esta activo el paginador y construye con base en éste
                this.config[3] ? await this.initialConfigurations() : this.buildBody(this.body);
                // ↑ si no esta activo el paginador construye la tabla sin modificaciones
                // ↓ verifica si está activo el buscador para crear copia del array completo
                this.config[2] ? this.table = this.body : null;
            } else {
                // ↓ Espera a que se pinte el HTML
                setTimeout(() => {
                    // ↓ Instancia los tooltips de materialize
                    const tooltips: NodeListOf<HTMLElement> = document.querySelectorAll(".datatable-tooltip");
                    M.Tooltip.init(tooltips);
                }, 100);
                // ↓ Verifica que el cuerpo tenga información
                if (event.body.currentValue.length > 0) {
                    // ↓ verifica si esta activo el paginador y construye la tabla con base en éste
                    this.config[3] ? await this.initialConfigurations() : this.buildBody(this.body);
                    // ↑ si no esta activo el paginador construye la tabla sin modificaciones
                    // ↓ verifica si está activo el buscador para crear copia del array completo
                    this.config[2] ? this.table = this.body : null;
                }
            }
        }
        // Verifica si se solicita descarga de la tabla
        if (event.download) {
            // Verifica que la solicitud la realice el usuario
            if (Number(event.download.currentValue) > 0) {
                // ↓↓ Abre la ventana emergente
                const modal: HTMLElement = document.querySelector('#modalForDownloadTable' + this.keyTable);
                M.Modal.getInstance(modal).open();
            } else {
                // ↓ Si la solicitud no la realiza el usuario define las opciones
                this.filterOptions = this.builder.group({
                    // ↓ Verifica que la entrada sea un booleano
                    filters: [true, Validators.compose([this.verifyBoolean])],
                    // ↓ Verifica que la entrada no esté vacía y que cumpla con una expresión regular
                    index: [0, Validators.compose([Validators.required, Validators.pattern(REGEXP.number)])]
                });
                // ↓* Configuraciones de materilizecss
                // ↓ Espera a que las opciones estén definidas
                setTimeout(() => {
                    const select: HTMLElement = document.querySelector('#downloadDataTable' + this.keyTable);
                    const modal: HTMLElement = document.querySelector('#modalForDownloadTable' + this.keyTable);
                    M.Modal.init(modal, { inDuration: 400, outDuration: 500, onCloseEnd: () => { this.fileAction.emit(false); } });
                    M.FormSelect.init(select, {});
                }, 0);
            }
        }
        // ↓ Guarda copia del valor inicial
        if (event.hideElements) { this.variableHideElements = this.hideElements; }
        // ↓ Guarda copia del valor inicial
        if (event.hideTitles) { this.variableHideTitles = this.hideTitles; }
        // ↓ Verifica si se activa la opciónde mostrar/ocultar columnas
        if (event.hiddenGroups) {
            // ↓↓ Reinicia la configuración inicial de los listados para ocultar
            this.hideElements = this.variableHideElements;
            this.hideTitles = this.variableHideTitles;
            // hiddenValues: Grupos de columnas a ocultar
            const hiddenValues: number[] = this.hiddenGroups.length > 0 ? this.hiddenGroups.join(",").split(",").map((e: string) => +e) : [];
            // grouped: Listado de columnas
            const grouped: number[] = Array.from({ length: this.tLength }, (u: undefined, i: number) => i);
            // notSelectedColumns: Columnas que no están en ningún grupo
            const notSelectedColumns: number[] = grouped.filter((e: number) => !hiddenValues.includes(e));
            // ↓ Añade al grupo de columnas las columnas que no se seleccionaron
            notSelectedColumns.length > 0 ? this.hiddenGroups.push(notSelectedColumns) : null;
            setTimeout(() => {
                // ↓* Instancia los dropdown de materialize
                const dropdown: NodeListOf<HTMLElement> = document.querySelectorAll('.datatable-dropdown');
                dropdown.forEach((elm: HTMLElement) => M.Dropdown.getInstance(elm) ? M.Dropdown.getInstance(elm).destroy() : null);
                M.Dropdown.init(dropdown, { closeOnClick: false, inDuration: 400, outDuration: 500 });
            }, 0);
        }
    }

    /* checkSelection(): Método que muestra/oculta columnas
        target: Elemento del formulario
        type: Tipo de opción
            0. Todos
            1. Individual
        group: Grupo de columnas a la que pertenece
    */
    checkSelection = (target: EventTarget, type: number, group: number) => {
        // toogle: Función que oculta/muestra las columnas en la tabla
        const toogle = (elm: HTMLInputElement) => {
            // element: Verifica si la columna existe en el listado de columnas a ocultar del cuerpo
            const element: number = (this.hideElements || []).indexOf(+elm.value);
            // element: Verifica si la columna existe en el listado de columnas a ocultar de la cabecera
            const title: number = (this.hideTitles || []).indexOf(+elm.value);
            // ↓ Muestra/Oculta las columnas en la tabla
            if (elm.checked) {
                // ↓↓ Muestra la columna
                if (element > -1) { this.hideElements.splice(element, 1); }
                if (title > -1) { this.hideTitles.splice(title, 1); }
            } else {
                // ↓↓ Oculta la columna
                if (element === -1) { this.hideElements ? this.hideElements.push(+elm.value) : this.hideElements = [+elm.value]; }
                if (title === -1) { this.hideTitles ? this.hideTitles.push(+elm.value) : this.hideTitles = [+elm.value]; }
            }
        };
        // ↓ Verifica que la opción sea Todos
        if (type === 0) {
            // ↓ Aplica el estado a todos los elementos
            document.querySelectorAll(`.hiddenCheckbox.group-${group}`).forEach((elm: HTMLInputElement) => {
                // ↓ Asigna el estado a los inputs
                elm.checked = (target as HTMLInputElement).checked;
                // ↓ Verifica que el valor no sea el de un grupo
                if (target as HTMLInputElement !== elm) { toogle(elm); }
                // ↑ Aplica el cambio a la tabla
            });
        } else {
            // ↓ Si es una opción individual se deselecciona el campo
            (document.querySelector(`.hiddenCheckbox.group-${group}`) as HTMLInputElement).checked = false;
            // ↓ Aplica el cambio a la tabla
            toogle(target as HTMLInputElement);
        }
        // ↓↓ Actualiza la referencia
        this.hideElements = [...(this.hideElements || [])];
        this.hideTitles = [...(this.hideTitles || [])];
        // ↓↓ Si el listado queda vacío convierte a nulo
        this.hideElements.length === 0 ? this.hideElements = null : null;
        this.hideTitles.length === 0 ? this.hideTitles = null : null;
    }

    /* verifyBoolean() Método que verifica que la entrada de un formulario sea booleano
        elm: Valor ingresado en formulario
    */
    verifyBoolean(elm: FormControl): BooleanValues {
        // ↓ Si es un valor booleano permite, de lo contrario marca error
        return typeof elm.value !== "boolean" ? { res: true } : null;
    }

    // initialConfigurations(): Método que establece las configuraciones de la tabla
    initialConfigurations() {
        // ↓ Establece la cantidad de filas
        this.slice = this.slice ? this.slice : this.list[0];
        // ↓ Gaurda la configuración inicial de la cantidad de filas
        this.variableSlice = this.variableSlice ? this.variableSlice : this.list[0];
        // ↓ Construye la tabla
        this.buildTable(0, this.slice);
        // ↓ Espera a que se construya la tabla
        setTimeout(() => {
            // ↓ Si está activado seleccionar filas inicia materialize
            if (this.config[1]) { M.FormSelect.init(document.querySelector('#selectDataTable' + this.keyTable), {}); }
        }, 0);
    }

    /* buildTable() Construye la tabla
        start: Posición de la matriz del cuerpo desde donde se empezará a mostrar
        slice: Cantidad de filas a mostrar a partir de start
    */
    buildTable(start: number, slice: number | string) {
        // ↓ verifica que el cuerpo de la tabla tenga elementos
        if (this.body.length > 0) {
            // ↓ Verifica si el valor de slice es all, entonces se mostrarán todas las filas del cuerpo
            slice === "all" ? slice = this.body.length : null;
            // ↓ Calcula la cantidad de páginas de acuerdo al recorte del cuerpo (Total filas dividido entre filas mostradas)
            this.pags = new Array(Math.ceil(this.body.length / Number(this.slice)));
            // ↓ Guarda el cuerpo con logitud recortada
            const table = this.body.slice(start, Number(slice));
            // ↓ Construye la tabla
            this.buildBody(table);
        } else {
            // ↓↓↓ Si el cuerpo está vacío reinicia las variables
            this.tableBody = [];
            this.pags = [];
            this.slice = this.list[0];
        }
    }

    /* buildBody(): Método que recibe el contenido filtrado para mostrar en la tabla.
        body: Cuerpo de la tabla
    */
    buildBody(body: ({ [undetermindeIndex: string]: any } | string[])[]) { this.tableBody = body; }

    /* refreshTable(): Método que realiza el cambio de página según la opción seleccionada en el paginador
        val: Número de página
    */
    refreshTable(val: number) {
        // ↓ Si val no está definido o es un número menor a 1 o es una página que no exite no cambia de página
        val = !val ? this.currentPage : (val <= 0 ? this.currentPage : (val > this.pags.length ? this.currentPage : val));
        // ↑ Si val está definido, es mayor a 0 y la opción esxite entonces cambia de página
        // ↓ actualiza la página actual de la tabla
        this.currentPage = Number(val);
        // ↓ Construye la tabla con base a la página seleccionada
        this.buildTable(((val * Number(this.slice)) - Number(this.slice)), (val * Number(this.slice)));
    }

    /* reziseTable(): Método que actualiza la tabla con base en la selección de filas a mostrar
        val: Cantidad de filas por página
    */
    reziseTable(val: number | string) {
        // ↓ Verifica si el valor de slice es all, entonces se mostrarán todas las filas del cuerpo
        val === "all" ? this.slice = this.body.length : this.slice = val;
        // ↑ Si el valor de slice es un número, muestra la cantida de filas selccionada
        // ↓ Reinicia el paginador desde la página 1
        this.currentPage = 1;
        // ↓ Actualiza la tabla
        this.refreshTable(this.currentPage);
    }

    /* searchInTable(): Método que muestra filas con información que se asemeje a la selección
        val: Texto a buscar
    */
    searchInTable(val: string) {
        val = new NormalizeTextPipe().transform(val);
        // ↓ Guarda la información original del cuerpo
        this.body = this.table;
        // keys: Guarda las propiedades del cuerpo
        const keys = Object.keys(this.body[0]);
        // table: Crea un nuevo cuerpo para mostrar
        let table = [];
        // ↓ Busca coincidencias en todas las columnas y filas
        this.body.filter((dta: ({ [undetermindeIndex: string]: any } | string[])) => {
            // find: bandera para saber si alguna columna de la fila coincide con el texto buscado
            let find: boolean;
            // ↓ Revisa todas las columnas
            keys.forEach((key: string) => {
                // Verifica coincidencias ignorando mayúsculas, minúsculas y caracteres especiales
                if (dta[key] && new NormalizeTextPipe().transform(dta[key].toString()).toLowerCase().includes(val.toLowerCase())) {
                    find = true; return;
                }
            });
            // ↓ si find es verdadero agrega la fila al nuevo cuerpo
            find === true ? table.push(dta) : null;
        });
        // ↓ Reinicia el paginador desde la página 1
        this.currentPage = 1;
        // Asigna el nuevo cuerpo a la tabla
        this.body = table;
        // ↓↓ Aplica las configuraciones de la tabla
        this.config[3] ? table = this.body.slice(0, Number(this.slice)) : table = this.body;
        this.config[3] ? this.pags = new Array(Math.ceil(this.body.length / Number(this.slice))) : null;
        // ↓ Construye la tabla
        this.buildBody(table);
    }

    /* generateHexString(): Genera el id único que se utilizará en la tabla
        length: Longitud del id
    */
    generateHexString(length: number): string {
        // ret: Guarda el id
        let ret = "";
        // ↓ Añade texto aleatorio
        while (ret.length < length) { ret += Math.random().toString(16).substring(2); }
        // ↓ Devuelve el id con la longitud indicada
        return ret.substring(0, length);
    }

    /* sendItem() Método que devuelve al componente la información de la fila seleccionada
        item: Fila seleccionada del cuerpo
    */
    sendItem(item: ({ [undetermindeIndex: string]: any } | string[])) { this.sendValue.emit(item); }

    /* activateTable(): Método que modifica la configuración de la tabla cuando el usuario muetsra/oculta las opciones
        evt: Información del evento click
    */
    async activateTable(evt: MouseEvent) {
        // ↓ Referencia del botón
        const target: HTMLElement = (evt.target as HTMLElement);
        // ↓ Verifica el texto para saber si debe mostrar las opciones
        if (!target.classList.value.includes("icon-active")) {
            // ↓ Activa el botón
            target.classList.toggle("icon-active");
            // ↓ Actualiza el cuerpo
            this.table = this.body;
            // ↓ Guarda la configuración inicial
            this.variableConfig = this.config;
            // ↓ Muestra todas las opciones
            this.config = Array.from({ length: 4 }, () => true);
            // ↓ Actualiza las filas a mostrar
            this.slice = this.list[0];
            // ↓ Construye la tabla
            await this.buildTable(0, this.slice);
            // ↓ Espera a que la tabla se construya
            setTimeout(() => {
                // inicia materialize
                if (this.config[1]) { M.FormSelect.init(document.querySelector('#selectDataTable' + this.keyTable), {}); }
            }, 0);
        } else {
            // ↑ Verifica si debe ocultar las opciones
            // ↓ Inactiva el botón
            target.classList.toggle("icon-active");
            // (↓^5) Restablece las configuraciones iniciales y actualiza la tabla
            this.body = this.table;
            this.config = this.variableConfig || [true, false, false, false];
            this.variableSlice ? this.slice = this.variableSlice : this.slice = this.list[0];
            this.config === [true, false, false, false] ? this.buildBody(this.body) : this.buildTable(0, this.slice);
            this.table = [];
        }
    }

    /* eventScroll() Método que vuelve horizontal el movimiento vertical de la rueda del mouse
        event: Información del evento de desplazamiento
    */
    eventScroll(event: WheelEvent) {
        // Verifica si la opción de responsiva está definida y que el tamaño de la pantalla sea de móvil o tablet
        if (this.responsive && window.screen.width <= 992) {
            // ↓ Aplica horizontalmente la mitad del scroll vertical
            document.querySelector('#responsiveBody' + this.keyTable).scrollLeft += (event.deltaY / 2);
            // ↓ Cancela el evento original
            event.preventDefault();
        }
    }

    // downloadFile(): Método que descarga la información de la tabla según las opciones seleccionadas
    downloadFile() {
        if (this.filterOptions.valid) {
            // selected: guarda la información del tipo de archivo seleccionado
            const selected: TableFile = this.files[this.filterOptions.value.index];
            // theme: Guarda los estilos actuales
            const theme: AnyValues = { ...this.currentTheme.themeValues };
            // ↓ Añade los colores secundarios
            [theme.group, theme.rules, theme.bodyRules, theme.hideRules, theme.colspanRules] = [this.currentTheme.themeGroups, this.thClasses, this.highlight, [this.hideTitles || [], this.hideElements || []], this.colspan || []];
            // styles: Guarda en texto los estilos actuales para dar color a las cabeceras
            const styles: string = JSON.stringify(theme);
            // parm: Parámetros para construir el archivo
            let parm: AnyValues = { styles };
            switch (selected.type) {
                // ↓ Verifica el archivo a descargar
                case "Hoja de Cálculo":
                    // sheets: Guarda en texto el nombre de los libros cuando están definidos
                    const sheets: string = JSON.stringify(selected.sheets || []);
                    // body: Guarda en texto el cuerpo de la tabla
                    const body: string = JSON.stringify([this.filterOptions.value.filters ? this.tableBody : (this.table.length > 0 ? this.table : this.body)]);
                    // headers: Guarda en texto las cabeceras de la tabla
                    const headers: string = JSON.stringify([this.headers]);
                    // footers: Guarda en texto el pie de página de la tabla
                    const footers: string = JSON.stringify([this.footers]);
                    // ↓ Asigna los parámetros
                    parm = { ...parm, body, headers, footers, operation: 1, sheets };
                    // ↓ Genera el archivo
                    this.getfile(parm, selected);
                    break;
                case "PDF":
                    // ↓↓ Paginación actual
                    const currentSlice: (number | string) = this.slice;
                    const currentPage: (number | string) = this.currentPage;
                    // ↓ Muestra todos los registros de la tabla
                    if (!this.filterOptions.value.filters) { this.buildTable(0, "all"); }
                    setTimeout(() => {
                        // text: Contenido HTML que se convertirá a pdf
                        const text: string = document.querySelector(`#datatable-form${this.keyTable}`).innerHTML;
                        // newContainer: Contenido HTML con los ajustes para generar pdf requerido
                        const newContainer: HTMLElement = document.createElement("div");
                        // ↓ Asigna el contenido HTML sin comentarios
                        newContainer.innerHTML = `${selected.title}<form>${this.uncommentHtml.transform(text)}<form>`;
                        // ↓ Elimina algunos elementos que no se deben mostrar
                        newContainer.querySelectorAll(".material-icons").forEach((elm: HTMLElement) => elm.remove());
                        // ↓ Define la operación a realizar en el API
                        parm.operation = 2;
                        // ↓ Asigna el contenido a imprimir
                        parm.body = JSON.stringify([newContainer.innerHTML]);
                        // ↓ Asigna la cabecera del documento
                        parm.headers = JSON.stringify(["gcgreportes.jpg"]);
                        // ↓ Asigna los estilos a aplicar
                        parm.pdfStyles = JSON.stringify([`<style>
                            form table tbody tr td.text-color-danger { color: red !important; text-align: center !important; }
                            form table tbody tr td.text-color-success { color: green !important; text-align: center !important; }
                            form table tbody tr td.text-color-warning { color: darkorange !important; text-align: center !important; }
                            .bold { font-weight: 700; }
                            form table thead tr:nth-child(1) th {
                              background: ${this.currentTheme.themeValues.background} !important;
                              color: ${this.currentTheme.themeValues.textColorOverBackground} !important;
                            }
                            form table thead tr:nth-child(1) th.background-theme-type-1 {
                                background: ${this.currentTheme.themeGroups[0]} !important;
                            }
                            form table thead tr:nth-child(1) th.background-theme-type-2 {
                                background: ${this.currentTheme.themeGroups[1]} !important;
                            }
                            form table thead tr:nth-child(1) th.background-theme-type-3 {
                                background: ${this.currentTheme.themeGroups[2]} !important;
                            }
                            form table thead tr:nth-child(1) th.background-theme-type-4 {
                                background: ${this.currentTheme.themeGroups[3]} !important;
                            }
                            .row { width: 100%; }
                            tbody tr:nth-child(odd) td:not(.background-color-danger):not(.background-color-warning):not(.background-color-success) {
                              background-color: #eee;
                            }
                            h1, h2, h3, h4, h5, h6 { text-align: center; }
                            .col { float: left; margin: 0 1.5%; }
                            form table { width: 100%; border: 1px solid #ddd; font-size: 10px; margin-bottom: 5px; }
                            form table thead tr th { border-bottom: 1px solid #ddd; text-align: center; }
                            form table tbody tr td { border-right: 1px solid #ddd; text-align: center; }
                            form table tfoot tr td { border-top: 1px solid #ddd; text-align: center; }
                        </style>`]);
                        // ↓ Genera el archivo
                        this.getfile(parm, selected);
                        // ↓ Devuelve la tabla a su estado natural
                        this.buildTable(0, currentSlice);
                        this.refreshTable(currentPage);
                    }, 100);
                    break;
            }
        }
    }

    /* getfile(): Método que ejecuta la descarga del archivo generado desde el API
        parm: Parámetros para enviar al API
        selected: Información del archivo a descargar
    */
    getfile(parm: AnyValues, selected: TableFile) {
        // ↓ Envía la información al API para que este construya el archivo
        this.callApiMethods.sendAsynchronousRequest("management", "buildReports", parm).then((data: Blob) => {
            // currentDate: Fecha y hora actuales
            const currentDate = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toJSON().slice(0, 19).replace(/[-:]/g, '').replace(/[T]/g, '_');
            // link: Elemento HTML para la descarga
            const link: HTMLAnchorElement = document.createElement('a');
            // ↓ Agrega la url del archivo al link
            link.href = window.URL.createObjectURL(data);
            // ↓ Asigna el nombre del archivo
            link.download = `${currentDate}_${selected.name}.${selected.extension}`;
            // ↓ Ejecuta la descarga
            document.body.appendChild(link); link.click(); link.remove();
        }).catch(this.callApiMethods.setError);
    }

    // closeModal(): Método que cierra la ventana emergente con las opciones de descarga
    closeModal() {
        // ↓↓ Cierra el modal
        const modal: HTMLElement = document.querySelector('#modalForDownloadTable' + this.keyTable);
        M.Modal.getInstance(modal).close();
    }

    /* trackLoop(): Método que verifica el índice de las filas para el loop de la tabla
        index: Información de la fila
    */
    trackLoop = (index: number) => index;

    ngOnDestroy() {
        // ↓* Destruye las instancias de materializecss
        const modal: HTMLElement = document.querySelector('#modalForDownloadTable' + this.keyTable);
        const dropdown: NodeListOf<HTMLElement> = document.querySelectorAll('.datatable-dropdown');
        const options: HTMLElement = document.querySelector('#downloadDataTable' + this.keyTable);
        const tooltips: NodeListOf<HTMLElement> = document.querySelectorAll("datatable-tooltip");
        const select: HTMLElement = document.querySelector('#selectDataTable' + this.keyTable);
        dropdown.forEach((elm: HTMLElement) => M.Dropdown.getInstance(elm) ? M.Dropdown.getInstance(elm).destroy() : null);
        tooltips.forEach((elm: HTMLElement) => M.Tooltip.getInstance(elm) ? M.Tooltip.getInstance(elm).destroy() : null);
        (options && M.FormSelect.getInstance(options)) ? M.FormSelect.getInstance(options).destroy() : null;
        (select && M.FormSelect.getInstance(select)) ? M.FormSelect.getInstance(select).destroy() : null;
        (modal && M.Modal.getInstance(modal)) ? M.Modal.getInstance(modal).destroy() : null;
    }

}
